import type { Menu, MenuModule } from '/@/router/types';
import type { RouteRecordNormalized } from 'vue-router';

import { useAppStoreWithOut } from '/@/store/modules/app';
import { usePermissionStore } from '/@/store/modules/permission';
import { transformMenuModule, getAllParentPath } from '/@/router/helper/menuHelper';
import { filter } from '/@/utils/helper/treeHelper';
import { isUrl } from '/@/utils/is';
import { router } from '/@/router';
import { PermissionModeEnum } from '/@/enums/appEnum';
import { RouterEnum } from '/@/enums/routerEnum';
import { pathToRegexp } from 'path-to-regexp';

const modules = import.meta.globEager('./modules/**/*.ts');

const menuModules: MenuModule[] = [];

Object.keys(modules).forEach((key) => {
  const mod = modules[key].default || {};
  const modList = Array.isArray(mod) ? [...mod] : [mod];
  menuModules.push(...modList);
});

// ===========================
// ==========Helper===========
// ===========================

const getPermissionMode = () => {
  const appStore = useAppStoreWithOut();
  return appStore.getProjectConfig.permissionMode;
};
const isBackMode = () => {
  return getPermissionMode() === PermissionModeEnum.BACK;
};

const isRouteMappingMode = () => {
  return getPermissionMode() === PermissionModeEnum.ROUTE_MAPPING;
};

const isRoleMode = () => {
  return getPermissionMode() === PermissionModeEnum.ROLE;
};

const staticMenus: Menu[] = [];
(() => {
  menuModules.sort((a, b) => {
    return (a.orderNo || 0) - (b.orderNo || 0);
  });

  for (const menu of menuModules) {
    staticMenus.push(transformMenuModule(menu));
  }
})();

/**
 * 唐鑫 2022-4-1
 * 返回兼容模式下的TOP_MENUS
 * @returns 返回兼容模式下的TOP_MENUS
 */
export async function getAsyncTopMenus() {
  const permissionStore = usePermissionStore();
  // console.log(permissionStore.getTopMenuList,'permissionStore.getTopMenuList')
  return permissionStore.getTopMenuList;
}

/**
 * 唐鑫 2022-4-1
 * 修改获取当前最新路由菜单方法，增加根据当前路由所属业务模块加载对应的路由菜单
 * @returns 返回当前最新路由菜单
 */
async function getAsyncMenus(__routerType: RouterEnum = RouterEnum.NONE) {
  const permissionStore = usePermissionStore();
  // if (isBackMode()) {
  //   return permissionStore.getBackMenuList.filter((item) => !item.meta?.hideMenu && !item.hideMenu);
  // }

  const calcRouterType =
    __routerType === RouterEnum.NONE ? permissionStore.getCurrentRouterType : __routerType;
  // 递归处理左侧菜单数据，meta.authorization为true标识有权限显示菜单
  function getMenuList(arr) {
    return arr
      .filter((item) => {
        return (
          !item.hideMenu &&
          item.meta &&
          item.meta.authorization &&
          (!item.meta.routerType ||
            item.meta.routerType === RouterEnum.NONE ||
            item.meta.routerType === calcRouterType)
        );
      })
      .map((item) => {
        item = Object.assign({}, item);
        if (item.children) {
          item.children = getMenuList(item.children);
        }
        return item;
      });
  }
  
  if (isRouteMappingMode()) {
    // return permissionStore.getFrontMenuList.filter((item) => !item.hideMenu);
    return permissionStore.getFrontMenuList.filter(
      (item) =>
        !item.hideMenu &&
        item.meta &&
        item.meta.authorization 
        // &&
        // // 这里允许无routerType，此路由配置在所有模块下都会显示
        // (!item.meta.routerType ||
        //   item.meta.routerType === RouterEnum.NONE ||
        //   item.meta.routerType === calcRouterType),
    );
  }

  if (isBackMode()) {
    return getMenuList(permissionStore.getBackMenuList);
  }

  return staticMenus;
}

export const getMenus = async (__routerType: RouterEnum = RouterEnum.NONE): Promise<Menu[]> => {
  const menus = await getAsyncMenus(__routerType);
  if (isRoleMode()) {
    const routes = router.getRoutes();
    return filter(menus, basicFilter(routes));
  }
  return menus;
};

export async function getCurrentParentPath(
  currentPath: string,
  __routerType: RouterEnum = RouterEnum.NONE,
) {
  const menus = await getAsyncMenus(__routerType);
  const allParentPath = await getAllParentPath(menus, currentPath);
  return allParentPath?.[0];
}

// Get the level 1 menu, delete children
export async function getShallowMenus(__routerType: RouterEnum = RouterEnum.NONE): Promise<Menu[]> {
  const menus = await getAsyncMenus(__routerType);
  const shallowMenuList = menus.map((item) => ({ ...item, children: undefined }));
  if (isRoleMode()) {
    const routes = router.getRoutes();
    return shallowMenuList.filter(basicFilter(routes));
  }
  return shallowMenuList;
}

// Get the children of the menu
export async function getChildrenMenus(
  parentPath: string,
  __routerType: RouterEnum = RouterEnum.NONE,
) {
  const menus = await getMenus(__routerType);
  const parent = menus.find((item) => item.path === parentPath);
  if (!parent || !parent.children || !!parent?.meta?.hideChildrenInMenu) {
    return [] as Menu[];
  }
  if (isRoleMode()) {
    const routes = router.getRoutes();
    return filter(parent.children, basicFilter(routes));
  }
  return parent.children;
}

function basicFilter(routes: RouteRecordNormalized[]) {
  return (menu: Menu) => {
    const matchRoute = routes.find((route) => {
      if (isUrl(menu.path)) return true;

      if (route.meta?.carryParam) {
        return pathToRegexp(route.path).test(menu.path);
      }
      const isSame = route.path === menu.path;
      if (!isSame) return false;

      if (route.meta?.ignoreAuth) return true;

      return isSame || pathToRegexp(route.path).test(menu.path);
    });

    if (!matchRoute) return false;
    menu.icon = (menu.icon || matchRoute.meta.icon) as string;
    menu.meta = matchRoute.meta;
    return true;
  };
}
